import React, { useEffect, useMemo, useState } from 'react';
import { audioList, audioUpload, audioDelete } from '../api/audio';
import './AudioFilesPage.scss';

export default function AudioFilesPage() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyIds, setBusyIds] = useState(new Set());
    const [queue, setQueue] = useState([]); // {file, title, progress, status, server}
    const totalUploading = useMemo(() => queue.filter(q => q.status === 'uploading').length, [queue]);

    useEffect(() => { load(); }, []);
    async function load() {
        setLoading(true);
        try { setFiles(await audioList()); }
        catch { alert('Ошибка загрузки списка аудио'); }
        finally { setLoading(false); }
    }

    const toTitle = (name) => (name || '').replace(/\.[^/.]+$/,'').trim() || 'Без названия';

    function onSelect(e) {
        const list = Array.from(e.target.files || []);
        if (!list.length) return;
        const entries = list.map(f => ({ file: f, title: toTitle(f.name), progress: 0, status: 'queued', server: null }));
        setQueue(q => [...q, ...dedup(q, entries)]);
        e.target.value = '';
    }

    function onDrop(e) {
        e.preventDefault();
        if (!e.dataTransfer?.files?.length) return;
        const list = Array.from(e.dataTransfer.files);
        const entries = list.map(f => ({ file: f, title: toTitle(f.name), progress: 0, status: 'queued', server: null }));
        setQueue(q => [...q, ...dedup(q, entries)]);
    }
    function onDragOver(e){ e.preventDefault(); }

    function dedup(existing, added) {
        const key = (it) => `${it.file.name}__${it.file.size}`;
        const set = new Set(existing.map(key));
        return added.filter(it => !set.has(key(it)));
    }

    async function startUploads() {
        const snapshot = queue.slice();
        const targets = snapshot.map((x,i)=> x.status==='queued'?i:-1).filter(i=>i>=0);
        if (!targets.length) return;
        setQueue(q => q.map((x,i) => targets.includes(i)?{...x,status:'uploading',progress:0}:x));

        for (const i of targets) {
            const it = snapshot[i];
            try {
                const server = await audioUpload(it.file, p => {
                    setQueue(q => {
                        const copy = q.slice();
                        if (copy[i]) copy[i] = { ...copy[i], progress: p, status: 'uploading' };
                        return copy;
                    });
                });
                setQueue(q => {
                    const copy = q.slice();
                    if (copy[i]) copy[i] = { ...copy[i], progress: 100, status: 'done', server };
                    return copy;
                });
            } catch {
                setQueue(q => {
                    const copy = q.slice();
                    if (copy[i]) copy[i] = { ...copy[i], status: 'error' };
                    return copy;
                });
            }
        }
        await load();
    }

    async function remove(id) {
        const next = new Set(busyIds); next.add(id); setBusyIds(next);
        try { await audioDelete(id); await load(); }
        catch { alert('Не удалось удалить'); }
        finally { const n = new Set(busyIds); n.delete(id); setBusyIds(n); }
    }

    function fmtSize(bytes) {
        if (bytes == null) return '—';
        const mb = bytes / 1024 / 1024;
        return `${mb.toFixed(2)} MB`;
    }

    return (
        <div className="audiofiles-page" onDrop={onDrop} onDragOver={onDragOver}>
            <div className="head">
                <h2>Аудиофайлы</h2>
                <label className="upload-btn">
                    <input type="file" accept="audio/*" multiple onChange={onSelect} />
                    Выбрать файлы
                </label>
                <button className="primary" onClick={startUploads} disabled={!queue.some(q => q.status==='queued')}>
                    Загрузить
                </button>
            </div>

            {queue.length > 0 &&
            <div className="upload-queue">
                {queue.map((q,i)=>(
                    <div key={i} className={`uq-item ${q.status}`}>
                        <div className="uq-name">{q.file.name}</div>
                        <div className="uq-bar"><div className="uq-fill" style={{width:`${q.progress||0}%`}}/></div>
                        <div className="uq-meta"><span>{q.progress||0}%</span><span>{q.status}</span></div>
                    </div>
                ))}
            </div>
            }

            {loading ? <div className="loading">Загрузка...</div> : (
                <table className="tbl">
                    <thead><tr><th>ID</th><th>Имя</th><th>Размер</th><th>Hash</th><th>Действия</th></tr></thead>
                    <tbody>
                    {files.length===0 ? (
                        <tr><td colSpan="5" className="empty">Нет файлов</td></tr>
                    ) : files.map(f=>(
                        <tr key={f.id}>
                            <td>{f.id}</td><td>{f.filename}</td><td>{fmtSize(f.size)}</td><td className="mono">{f.hash}</td>
                            <td><button className="danger" disabled={busyIds.has(f.id)} onClick={()=>remove(f.id)}>
                                {busyIds.has(f.id)?'...':'Удалить'}
                            </button></td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}

            {totalUploading>0 && <div className="hint">Загрузка выполняется, не закрывайте страницу.</div>}
        </div>
    );
}
